import { useState } from 'react';
import { ApiError } from '../api/client';
import {
  useConfirmRetimeRequest,
  useCreateRetimeRequest,
  useDeleteRetimeRequest,
  useRetimeRequests,
  useUnconfirmRetimeRequest,
  useWorldOptions,
} from '../api/queries';
import type { RetimeRequest, User } from '../api/types';
import { Spinner } from '../components/Spinner';
import { useToast } from '../components/Toast';

function formatCoordinates(request: RetimeRequest): string {
  return `${request.villageX}|${request.villageY}`;
}

function formatArrival(request: RetimeRequest): string {
  return new Date(request.arrivalTimestamp).toLocaleString('de-DE');
}

/** `x|y` bzw. `x,y`, wie im Spiel üblich geschrieben -- z. B. "574|874". */
const COORDINATES_PATTERN = /^(\d{1,3})\s*[|,]\s*(\d{1,3})$/;

function parseCoordinates(value: string): { x: number; y: number } | null {
  const match = COORDINATES_PATTERN.exec(value.trim());
  if (!match) return null;
  return { x: Number(match[1]), y: Number(match[2]) };
}

/**
 * Retime-Anfragen-Seite: anders als SD-Anfragen OHNE Genehmigungsschritt --
 * jede/r verifizierte Mitglied sieht/stellt/bestätigt sofort (serverseitig
 * durchgesetzt über `requireVerifiedPlayer` ohne `requireTribeLeaderOrAdmin`,
 * siehe `retime-request.routes.ts`). Löschen bleibt der Erstellerin/dem
 * Ersteller sowie Stammesführern/Administratoren vorbehalten.
 */
export function RetimeRequestsPage({ user }: { user: User }) {
  const needsWorldChoice = user.world === null;
  const [worldKey, setWorldKey] = useState('');
  const worlds = useWorldOptions('login');
  const activeWorld = user.world?.key ?? worldKey;
  const isLeader = user.role === 'tribeLeader' || user.role === 'admin';

  const requests = useRetimeRequests(activeWorld || undefined);

  const rows = requests.data?.requests ?? [];

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h1>Retime-Anfragen</h1>
          <p className="muted">
            Um einen Retime auf ein Dorf bitten und bestätigen, wer selbst einen schickt -- ohne Genehmigung, für alle
            Mitglieder dieser Welt sofort sichtbar.
          </p>
        </div>
      </div>

      {needsWorldChoice && (
        <label className="field">
          <span>Welt</span>
          <select value={worldKey} onChange={(event) => setWorldKey(event.target.value)} required>
            <option value="" disabled>
              Welt wählen …
            </option>
            {(worlds.data?.worlds ?? []).map((world) => (
              <option key={world.key} value={world.key}>
                {world.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {needsWorldChoice && !worldKey && <p className="muted center pad">Wähle eine Welt, um ihre Retime-Anfragen zu sehen.</p>}

      {activeWorld && (
        <>
          <NewRetimeRequestForm worldKey={activeWorld} />

          {requests.isPending && <Spinner label="Retime-Anfragen werden geladen …" />}

          {requests.isError && (
            <div className="flash flash-error">
              {requests.error instanceof ApiError ? requests.error.message : 'Retime-Anfragen konnten nicht geladen werden.'}
            </div>
          )}

          <h2>Offene Retime-Anfragen</h2>
          {!requests.isPending && rows.length === 0 && (
            <p className="muted center pad">Aktuell keine offenen Retime-Anfragen.</p>
          )}
          {rows.map((request) => (
            <RetimeRequestRow
              key={request.id}
              request={request}
              worldKey={activeWorld}
              user={user}
              canDelete={isLeader || request.createdBy === user.id}
            />
          ))}
        </>
      )}
    </section>
  );
}

function NewRetimeRequestForm({ worldKey }: { worldKey: string }) {
  const [coordinates, setCoordinates] = useState('');
  const [arrivalTime, setArrivalTime] = useState('');
  const create = useCreateRetimeRequest(worldKey);
  const toast = useToast();

  const parsedCoordinates = parseCoordinates(coordinates);
  const parsedArrival = arrivalTime ? new Date(arrivalTime) : null;
  const valid = parsedCoordinates !== null && parsedArrival !== null && parsedArrival.getTime() > Date.now();

  const submit = () => {
    if (!parsedCoordinates || !parsedArrival || !valid) return;
    create.mutate(
      {
        villageX: parsedCoordinates.x,
        villageY: parsedCoordinates.y,
        arrivalTime: parsedArrival.toISOString(),
      },
      {
        onSuccess: () => {
          toast.push('success', 'Retime-Anfrage wurde gestellt.');
          setCoordinates('');
          setArrivalTime('');
        },
        onError: (error) => toast.push('error', error.message),
      },
    );
  };

  return (
    <div className="form" style={{ marginBottom: '16px' }}>
      <div className="field-row">
        <label className="field">
          <span>Dorf</span>
          <input
            type="text"
            value={coordinates}
            onChange={(event) => setCoordinates(event.target.value)}
            placeholder="574|874"
          />
        </label>
        <label className="field">
          <span>Ankunftszeit</span>
          <input
            type="datetime-local"
            step="1"
            value={arrivalTime}
            onChange={(event) => setArrivalTime(event.target.value)}
          />
        </label>
        <button type="button" className="btn btn-primary" disabled={!valid || create.isPending} onClick={submit}>
          {create.isPending ? 'Wird gestellt …' : 'Retime anfordern'}
        </button>
      </div>
      <small className="hint">Sofort für alle Mitglieder dieser Welt sichtbar, keine Genehmigung nötig.</small>
    </div>
  );
}

function RetimeRequestRow({
  request,
  worldKey,
  user,
  canDelete,
}: {
  request: RetimeRequest;
  worldKey: string;
  user: User;
  canDelete: boolean;
}) {
  const confirm = useConfirmRetimeRequest(worldKey);
  const unconfirm = useUnconfirmRetimeRequest(worldKey);
  const remove = useDeleteRetimeRequest(worldKey);
  const toast = useToast();

  const hasConfirmed = request.confirmations.some((c) => c.userId === user.id);

  const toggleConfirm = () => {
    if (hasConfirmed) {
      unconfirm.mutate(
        { id: request.id },
        {
          onSuccess: () => toast.push('success', 'Bestätigung wurde zurückgezogen.'),
          onError: (error) => toast.push('error', error.message),
        },
      );
    } else {
      confirm.mutate(
        { id: request.id },
        {
          onSuccess: () => toast.push('success', `Retime für ${formatCoordinates(request)} bestätigt.`),
          onError: (error) => toast.push('error', error.message),
        },
      );
    }
  };

  const onDelete = () => {
    if (!window.confirm(`Retime-Anfrage für ${formatCoordinates(request)} löschen?`)) return;
    remove.mutate(
      { id: request.id },
      {
        onSuccess: () => toast.push('success', 'Retime-Anfrage wurde entfernt.'),
        onError: (error) => toast.push('error', error.message),
      },
    );
  };

  return (
    <div className="card" style={{ marginBottom: '12px' }}>
      <div className="card-head">
        <div>
          <h3 style={{ margin: 0 }}>
            {formatCoordinates(request)} <span className="badge badge-pending">{formatArrival(request)}</span>
          </h3>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            Angefragt von {request.createdByName ?? '—'}
          </p>
        </div>
        {canDelete && (
          <button type="button" className="btn btn-danger btn-sm" disabled={remove.isPending} onClick={onDelete}>
            Löschen
          </button>
        )}
      </div>

      {request.confirmations.length > 0 && (
        <ul className="muted" style={{ margin: '8px 0', paddingLeft: '18px' }}>
          {request.confirmations.map((confirmation) => (
            <li key={confirmation.id}>{confirmation.userName ?? 'Unbekannt'}</li>
          ))}
        </ul>
      )}

      <button
        type="button"
        className={hasConfirmed ? 'btn btn-danger btn-sm' : 'btn btn-primary btn-sm'}
        disabled={confirm.isPending || unconfirm.isPending}
        onClick={toggleConfirm}
      >
        {hasConfirmed ? 'Zurückziehen' : 'Ich schicke einen Retime'}
      </button>
    </div>
  );
}
